import React, { useState, useEffect } from "react";
import { Box, Flex, Text, Button, Card, TextField } from "@radix-ui/themes";
import { BarChart3, Plus, Save } from "lucide-react";
import { IEntity } from "@/types/breeze/Entity";
import { PaginatedResult } from "@/types/breeze/Any";
import { chartConfigurationService } from "@/services/breeze/ChartConfigurationService";
import { ChartConfiguration } from "@/types/breeze/ChartConfiguration";
import { toast } from "react-hot-toast";
import ChartDashboard from "./charts/ChartDashboard";
import ChartCreator from "./charts/ChartCreator";

interface EntityChartsViewProps {
  entityDefinition: IEntity;
  entityData: PaginatedResult<Record<string, unknown>> | null;
  isLoading: boolean;
}

type ViewMode = "dashboard" | "creator";

function buildInitialDraft(entity: IEntity): ChartConfiguration {
  const hasCreatedAt = entity.fields.some((f) => f.name === "created_at");
  return {
    name: "",
    dataSource: "entity",
    entityName: entity.name,
    chartType: "column",
    xField: hasCreatedAt ? "created_at" : null,
    yField: null,
    pieMode: "count",
    yAgg: "count",
    xInterval: "day",
    timeframePreset: "all",
  };
}

function draftToRequest(draft: ChartConfiguration, entityName: string) {
  return {
    name: draft.name,
    dataSource: "entity" as const,
    entityName,
    chartType: draft.chartType,
    xField: draft.xField,
    yField: draft.yField,
    pieMode: draft.pieMode,
    stackBy: draft.stackBy,
    yAgg: draft.yAgg,
    xInterval: draft.xInterval,
    timeframePreset: draft.timeframePreset,
    timeframeFrom: draft.timeframeFrom,
    timeframeTo: draft.timeframeTo,
    timeframeOffsetDays: draft.timeframeOffsetDays ?? null,
    compareEnabled: draft.compareEnabled ?? null,
    comparePreviousDays: draft.comparePreviousDays ?? null,
    compareShowOverlay: draft.compareShowOverlay ?? null,
    limit: draft.limit,
    sortDirection: draft.sortDirection,
    cumulative: draft.cumulative,
    target: draft.target,
    targetMode: draft.targetMode,
    filters: draft.filters ?? null,
    funnelStages: draft.funnelStages ?? null,
    funnelStyle: draft.funnelStyle ?? null,
    funnelMode: draft.funnelMode ?? null,
    cohortField: draft.cohortField ?? null,
  };
}

export default function EntityChartsView({
  entityDefinition,
  entityData,
  isLoading,
}: EntityChartsViewProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("dashboard");
  const [draft, setDraft] = useState<ChartConfiguration>(() =>
    buildInitialDraft(entityDefinition),
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [savedCharts, setSavedCharts] = useState<ChartConfiguration[]>([]);
  const [editingChart, setEditingChart] = useState<ChartConfiguration | null>(
    null,
  );
  const [showAddChart, setShowAddChart] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const charts = await chartConfigurationService.getChartConfigurations(
          entityDefinition.name,
        );
        if (!cancelled) setSavedCharts(charts);
      } catch {
        if (!cancelled) toast.error("Failed to load saved charts");
      }
    })();
    setDraft(buildInitialDraft(entityDefinition));
    setEditingChart(null);
    return () => {
      cancelled = true;
    };
  }, [entityDefinition]);

  const handleSaveChart = async () => {
    setIsSaving(true);
    try {
      const request = draftToRequest(draft, entityDefinition.name);
      if (editingChart) {
        const updatedChart =
          await chartConfigurationService.updateChartConfiguration(
            editingChart.id!,
            request,
          );
        setSavedCharts((prev) =>
          prev.map((chart) =>
            chart.id === editingChart.id
              ? { ...editingChart, ...updatedChart, name: draft.name }
              : chart,
          ),
        );
        toast.success(`Chart "${draft.name}" updated successfully!`);
        setEditingChart(null);
      } else {
        const newChart =
          await chartConfigurationService.createChartConfiguration(request);
        setSavedCharts((prev) => [...prev, newChart]);
        toast.success(`Chart "${draft.name}" saved successfully!`);
      }
      setDraft((prev) => ({ ...prev, name: "" }));
    } catch {
      toast.error(`Failed to ${editingChart ? "update" : "save"} chart`);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Card>
          <Box p="6">
            <Text>Loading chart data...</Text>
          </Box>
        </Card>
      </Box>
    );
  }

  if (!entityData?.items?.length) {
    return (
      <Box>
        <Card>
          <Box p="6">
            <Flex direction="column" gap="3">
              <BarChart3 className="w-12 h-12 text-gray-400" />
              <Flex direction="column">
                <Text size="3" weight="medium" mb="2">
                  No data available
                </Text>
                <Text size="2" color="gray">
                  Add some data to this entity to see charts
                </Text>
              </Flex>
            </Flex>
          </Box>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="between" align={viewMode === "creator" ? "end" : "start"} gap="3" mb="6">
        {viewMode === "dashboard" ? (
          <>
            <Flex direction="column">
              <Text size="4" weight="medium" mb="1">
                Charts for {entityDefinition.name}
              </Text>
              <Text size="2" color="gray">
                {entityData.items.length} records available
              </Text>
            </Flex>
            <Flex gap="2">
              <Button variant="solid" size="2" onClick={() => setViewMode("dashboard")}>
                Dashboard
              </Button>
              <Button variant="outline" size="2" onClick={() => setShowAddChart(true)}>
                <Plus className="w-4 h-4" />
                Add Chart
              </Button>
            </Flex>
          </>
        ) : (
          <>
            <Button variant="soft" size="2" onClick={() => setViewMode("dashboard")}>
              ← Back
            </Button>
            <Flex direction="column" gap="1" style={{ flex: "1 1 auto", minWidth: 240 }}>
              <Text as="div" size="2" weight="medium" color="gray">Chart name</Text>
              <TextField.Root
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="Give this chart a name"
              />
            </Flex>
            <Button
              size="2"
              disabled={!draft.name.trim() || isSaving}
              onClick={handleSaveChart}
            >
              <Save className="w-4 h-4" />
              {isSaving
                ? editingChart ? "Updating…" : "Saving…"
                : editingChart ? "Update Chart" : "Save Chart"}
            </Button>
          </>
        )}
      </Flex>

      {viewMode === "dashboard" ? (
        <ChartDashboard
          entityData={entityData}
          savedCharts={savedCharts}
          showAddChart={showAddChart}
          onCreateChart={() => {
            setEditingChart(null);
            setDraft(buildInitialDraft(entityDefinition));
            setShowAddChart(false);
            setViewMode("creator");
          }}
          onCloseAddChart={() => setShowAddChart(false)}
        />
      ) : (
        <ChartCreator
          entityDefinition={entityDefinition}
          entityData={entityData}
          draft={draft}
          onDraftChange={setDraft}
        />
      )}
    </Box>
  );
}
